<template>
  <div class="cep-recalculate-participation-summary">
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">
              Elections Participation Summary (Step 2 for Elections)
            </div>
          </template>
        </es-header>
      </div>
      <es-loader v-if="loading || loadingSummary" style="margin: 6.5rem"/>
      <es-exception
        v-else-if="loadingError"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
        margin="0"
        style="padding: 6.5rem"/>
      <div v-else style="padding: 1rem">
        <div class="has-text-left banner">
          This section displays all sites identified as participating
          (Individual Participation or in a Cluster) in the Sites panel above.
          The data displayed in the table will be dependent on your selection
          in the Base Year Decision column.
          You must select a Base Year Decision for each site.
        </div>
        <div class="table-container">
          <table class="table is-fullwidth is-hoverable" style="margin-bottom: 0.25rem">
            <colgroup>
              <col width="1%">
              <col width="20%">
              <col>
              <col>
              <col>
              <col>
              <col>
              <col>
              <col>
              <col>
              <col>
              <col>
              <col width="10%">
            </colgroup>
            <thead>
              <tr class="header-banner">
                <th colspan="3"></th>
                <th colspan="3" class="header-banner-base">Current/Base year data</th>
                <th colspan="5" class="header-banner-current">Decision data</th>
                <th colspan="2" rowspan="2"></th>
              </tr>
            </thead>
            <es-sorter :columnHeaders="columnHeaders"/>
            <tbody>
              <tr v-if="!schools.length">
                <td colspan="13" class="exception-container">
                  <es-exception
                    exceptionMessage="No Elections participation available."
                    margin="0"
                    style="padding: 6.5rem"/>
                </td>
              </tr>
              <cep-recalculate-participation-row
                v-else
                v-for="school in schools"
                :key="school.schoolCode"
                :school="school"
                :viewOnly="electionsPermissions.viewOnly"
                @baseYearDecisionChanged="baseYearDecisionChanged"/>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div class="document cep-participation-summary-footer">
      <button class="button is-primary" :disabled="!isValid" @click="goToSubmission">
          Next Step (Step 3 Election Submission)
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CepRecalculateParticipationRow from '@/components/CepRecalculateParticipationRow.vue';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSorter from '@/components/EsSorter.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import {
  mapActions,
  mapGetters,
} from 'vuex';

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
  ],
  components: {
    CepRecalculateParticipationRow,
    EsException,
    EsHeader,
    EsLoader,
    EsSorter,
  },
  props: {
    locationGroupId: {
      type: [String, Number],
    },
    loadingSummary: {
      type: Boolean,
    },
    schoolYear: {
      type: Number,
    },
    electionsPermissions: {
      type: Object,
    },
    cepSummary: {
      type: Object,
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,
      schools: [],
      columnHeaders: [],
    };
  },
  async created() {
    await this.loadPersonTypes();
    this.columnHeaders = [
      {
        header: `${this.getFieldLabel(`PER:${this.defaultPersonType}:DIRECTMATCH:SCHOOL_CODE`)} Code`,
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Location Name',
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Total Sites',
        style: {
          borderRight: '1px solid #dee2e6',
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Current Base Year',
        style: {
          padding: '.1rem 0.75rem',
          borderRight: '1px solid #dee2e6',
        },
      },
      {
        header: 'Base Year ISP%',
        style: {
          padding: '.1rem 0.75rem',
          borderRight: '1px solid #dee2e6',
        },
      },
      {
        header: 'Current Year ISP%',
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Base Year Decision',
        style: {
          borderLeft: '1px solid #dee2e6',
          padding: '.1rem 0.75rem',
          whiteSpace: 'nowrap',
        },
      },
      {
        header: 'Base Year',
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Total Identified',
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Total Enrolled',
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'ISP %',
        style: {
          borderLeft: '1px solid #dee2e6',
          borderRight: '1px solid #dee2e6',
          padding: '.1rem 0.75rem',
          whiteSpace: 'nowrap',
        },
      },
      {
        header: 'Status',
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Quick Tips',
        style: {
          padding: '.1rem 0.75rem',
        },
      },
    ];
    this.onLoad();
  },
  watch: {
    locationGroupId() {
      this.onLoad();
    },
  },
  computed: {
    ...mapGetters('lookups', [
      'defaultPersonType',
    ]),
    isValid() {
      return this.electionsPermissions.submissionReady && !this.loadingError && !this.loading && this.schools.length && this.cepSummary.electionStatus !== 'A';
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    async onLoad() {
      try {
        this.loading = true;
        this.loadingError = false;
        const response = await axios.get(`cep/${this.locationGroupId}/elections/${this.schoolYear}/participation-summary`);
        this.schools = response.data;
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    async baseYearDecisionChanged(data) {
      this.loading = true;

      try {
        const { clusterId, stateLocationId } = data.school;
        const locationId = clusterId ? `clusterId=${clusterId}` : `schoolId=${stateLocationId}`;
        const response = await axios.put(`cep/${this.locationGroupId}/elections/action?&action=${data.value}&${locationId}`);
        const location = this.schools.find(
          s => (clusterId ? s.clusterId === clusterId : s.stateLocationId === stateLocationId),
        );
        if (location) {
          Object.assign(location, response.data);
        }
        this.$emit('baseYearDecisionChanged');
        this.showNotification('success', ['Base Year Decision has been changed.']);
      } catch (e) {
        const { status } = e.response;
        let apiMsg = '';
        if (status === 403) {
          apiMsg = 'Sorry, you are not authorized to access this content.';
        } else if (status === 400) {
          apiMsg = `There was a problem updating Base Year Decision. ${e.response.data.message}`;
        } else {
          apiMsg = 'There was an issue. Please try again or contact your administrator.';
        }
        this.showNotification('danger', [apiMsg]);
      } finally {
        this.loading = false;
      }
    },
    setBusy(value) {
      this.loading = value;
    },
    goToSubmission() {
      this.$router.push({ path: 'electionsSubmission', query: { locationGroupId: this.locationGroupId, schoolYear: this.schoolYear } });
    },
  },
};
</script>

<style lang="sass" scoped>
  .banner
    font-size: .85rem
    padding-left: 1.5rem
    padding-right: 0.5rem
    margin-bottom: 1rem
    color: #212529

  .table .header-banner th
    color: #fff
    height: 40px
    text-transform: uppercase
    text-align: center
    &.header-banner-base
      background-color: #595959
    &.header-banner-current
      background-color: #70ad47

  .table thead tr th
    padding: .1rem 0.75rem

  .exception-container
    text-align: center

  .cep-participation-summary-footer
    padding: 0.5rem 0
    text-align: right;
    padding-right: 0.25rem
    position: sticky
    bottom: 0
    z-index: 4

  .cep-recalculate-participation-summary
    display: unset

  .table-container
    overflow-x: auto
</style>
